import { useCallback, useEffect, useState } from "react";
import {
  Check,
  ChevronDown,
  ChevronRight,
  Loader2,
  Mail,
  Sparkles,
  UserPlus,
  X,
} from "lucide-react";
import { useNotify } from "ra-core";

import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Skeleton } from "@/components/ui/skeleton";
import { getSupabaseClient } from "../providers/supabase/supabase";
import { streamSkillRun } from "@/lib/agenticClient";

// Sprint 10.x — Validation queue pour les enrichissements de contact détectés
// automatiquement par la skill `enrich_emails_scan` (routine cron 10 min).
// Pour chaque mail entrant où le contact est "Pending Pending" (ou absent),
// l'agent propose un update / create. Théo valide manuellement ici.

type Approval = {
  id: string;
  kind: string;
  summary: string;
  payload: {
    email_id?: number;
    mode?: "update" | "create";
    contact_id?: number;
    contact_data?: {
      first_name?: string;
      last_name?: string;
      title?: string;
      background?: string;
      phone_jsonb?: Array<{ number: string; type: string }>;
      email_jsonb?: Array<{ email: string; type: string }>;
    };
  };
  created_at: string;
};

type RowState =
  | "pending"
  | "applying"
  | "done"
  | "rejecting"
  | "rejected"
  | "error";

type ApprovalRow = Approval & {
  state: RowState;
  error?: string;
  expanded?: boolean;
  emailSubject?: string;
  emailFrom?: string;
};

export const EmailEnrichmentCard = () => {
  const notify = useNotify();
  const [approvals, setApprovals] = useState<ApprovalRow[] | null>(null);
  const [loadErr, setLoadErr] = useState<string | null>(null);
  const [scanning, setScanning] = useState(false);
  const [scanError, setScanError] = useState<string | null>(null);

  const load = useCallback(async () => {
    setLoadErr(null);
    const supabase = getSupabaseClient();
    const { data, error } = await supabase
      .from("agentic_pending_approvals")
      .select("id,kind,summary,payload,created_at,status")
      .eq("kind", "enrich_contact_from_email")
      .eq("status", "pending")
      .order("created_at", { ascending: false })
      .limit(50);
    if (error) {
      setLoadErr(error.message);
      setApprovals([]);
      return;
    }
    const rows = (data ?? []) as Array<Approval & { status: string }>;
    // Fetch email subjects in batch
    const emailIds = rows
      .map((r) => r.payload?.email_id)
      .filter((id): id is number => typeof id === "number");
    let emailsById = new Map<
      number,
      { subject: string | null; from_email: string | null }
    >();
    if (emailIds.length > 0) {
      const { data: emailsData } = await supabase
        .from("email_messages")
        .select("id,subject,from_email")
        .in("id", emailIds);
      emailsById = new Map(
        (emailsData ?? []).map((e) => [
          (e as { id: number }).id,
          {
            subject: (e as { subject: string | null }).subject,
            from_email: (e as { from_email: string | null }).from_email,
          },
        ]),
      );
    }
    setApprovals(
      rows.map((r) => {
        const meta = r.payload?.email_id
          ? emailsById.get(r.payload.email_id)
          : null;
        return {
          ...r,
          state: "pending" as const,
          emailSubject: meta?.subject ?? undefined,
          emailFrom: meta?.from_email ?? undefined,
        };
      }),
    );
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  const scanNow = useCallback(async () => {
    setScanning(true);
    setScanError(null);
    try {
      for await (const ev of streamSkillRun("enrich_emails_scan", {
        limit: 10,
      })) {
        if (ev.event === "run.done") {
          const out = ev.data.output as {
            approvals_created?: number;
            summary_markdown?: string;
          };
          notify(
            `Scan terminé : ${out?.approvals_created ?? 0} enrichissement(s) proposé(s)`,
            { type: "success" },
          );
          await load();
        }
        if (ev.event === "run.error") {
          throw new Error(ev.data.error);
        }
      }
    } catch (e) {
      setScanError(e instanceof Error ? e.message : String(e));
    } finally {
      setScanning(false);
    }
  }, [load, notify]);

  const decide = useCallback(
    async (approval: ApprovalRow, action: "execute" | "reject") => {
      setApprovals((prev) =>
        (prev ?? []).map((a) =>
          a.id === approval.id
            ? { ...a, state: action === "execute" ? "applying" : "rejecting" }
            : a,
        ),
      );
      try {
        const supabase = getSupabaseClient();
        const {
          data: { session },
        } = await supabase.auth.getSession();
        if (!session) throw new Error("not_authenticated");
        const url = `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/agent-runtime/approvals/${approval.id}/${action}`;
        const res = await fetch(url, {
          method: "POST",
          headers: {
            Authorization: `Bearer ${session.access_token}`,
            "Content-Type": "application/json",
          },
        });
        if (!res.ok && res.status !== 409) {
          const body = await res.text().catch(() => "");
          throw new Error(`HTTP ${res.status}: ${body.slice(0, 200)}`);
        }
        setApprovals((prev) =>
          (prev ?? []).map((a) =>
            a.id === approval.id
              ? { ...a, state: action === "execute" ? "done" : "rejected" }
              : a,
          ),
        );
        notify(
          action === "execute" ? "Contact enrichi" : "Enrichissement refusé",
          { type: action === "execute" ? "success" : "info" },
        );
      } catch (e) {
        const msg = e instanceof Error ? e.message : String(e);
        setApprovals((prev) =>
          (prev ?? []).map((a) =>
            a.id === approval.id ? { ...a, state: "error", error: msg } : a,
          ),
        );
        notify(`Erreur : ${msg}`, { type: "error" });
      }
    },
    [notify],
  );

  const toggleExpand = useCallback((id: string) => {
    setApprovals((prev) =>
      (prev ?? []).map((a) =>
        a.id === id ? { ...a, expanded: !a.expanded } : a,
      ),
    );
  }, []);

  const pendingCount = (approvals ?? []).filter(
    (a) => a.state === "pending",
  ).length;

  return (
    <Card>
      <CardContent className="py-4 md:py-5">
        <div className="flex items-center gap-2 mb-2">
          <UserPlus className="size-4 text-fabrik-violet" />
          <p className="text-[13px] font-semibold text-foreground">
            Enrichissements à valider
          </p>
          {pendingCount > 0 && (
            <span className="text-[11px] text-muted-foreground">
              ({pendingCount})
            </span>
          )}
          <Button
            size="sm"
            variant="ghost"
            className="ml-auto h-7 px-2 text-xs gap-1"
            onClick={scanNow}
            disabled={scanning}
            title="Forcer un scan immédiat (cron auto toutes les 10 min)"
          >
            {scanning ? (
              <Loader2 className="size-3.5 animate-spin" />
            ) : (
              <Sparkles className="size-3.5" />
            )}
            Scanner
          </Button>
        </div>

        {approvals === null ? (
          <Skeleton className="h-16 w-full" />
        ) : loadErr ? (
          <p className="text-xs text-destructive">{loadErr}</p>
        ) : pendingCount === 0 ? (
          <p className="text-xs text-muted-foreground py-1">
            Aucun enrichissement en attente. L'agent scanne tes mails entrants
            toutes les 10 min.
          </p>
        ) : (
          <div className="space-y-2">
            {approvals
              .filter((a) => a.state !== "rejected")
              .map((a) => (
                <ApprovalRow
                  key={a.id}
                  approval={a}
                  onToggle={() => toggleExpand(a.id)}
                  onAccept={() => decide(a, "execute")}
                  onReject={() => decide(a, "reject")}
                />
              ))}
          </div>
        )}

        {scanError && (
          <p className="text-xs text-destructive mt-2">Scan : {scanError}</p>
        )}
      </CardContent>
    </Card>
  );
};

function ApprovalRow({
  approval,
  onToggle,
  onAccept,
  onReject,
}: {
  approval: ApprovalRow;
  onToggle: () => void;
  onAccept: () => void;
  onReject: () => void;
}) {
  const mode = approval.payload?.mode;
  const data = approval.payload?.contact_data ?? {};
  const fullName =
    [data.first_name, data.last_name].filter(Boolean).join(" ") || "—";

  return (
    <div className="rounded border border-amber-500/30 bg-amber-500/5 dark:bg-amber-500/10 p-2">
      <div className="flex items-start gap-2">
        <button
          type="button"
          onClick={onToggle}
          className="flex-1 min-w-0 text-left flex items-start gap-1.5 hover:bg-amber-500/10 rounded px-1 -mx-1 -my-0.5 py-0.5"
          title={approval.expanded ? "Replier" : "Voir les détails"}
        >
          {approval.expanded ? (
            <ChevronDown className="size-3.5 mt-0.5 shrink-0 text-muted-foreground" />
          ) : (
            <ChevronRight className="size-3.5 mt-0.5 shrink-0 text-muted-foreground" />
          )}
          <div className="flex-1 min-w-0">
            <div className="text-xs font-medium truncate flex items-center gap-1.5">
              {mode === "create" ? (
                <span className="inline-flex items-center gap-0.5 text-emerald-700 dark:text-emerald-400">
                  <UserPlus className="size-3" /> Créer
                </span>
              ) : (
                <span className="inline-flex items-center gap-0.5 text-blue-700 dark:text-blue-400">
                  <UserPlus className="size-3" /> Enrichir #
                  {approval.payload?.contact_id ?? "?"}
                </span>
              )}
              <span className="text-foreground truncate">{fullName}</span>
            </div>
            <div className="text-[11px] text-muted-foreground truncate flex items-center gap-1">
              <Mail className="size-3 shrink-0" />
              {approval.emailFrom ?? "?"} ·{" "}
              {approval.emailSubject ?? "(sans objet)"}
            </div>
          </div>
        </button>
        {approval.state === "pending" && (
          <div className="flex gap-1 shrink-0">
            <Button
              size="sm"
              variant="ghost"
              className="h-7 px-2"
              onClick={onReject}
            >
              <X className="size-3.5" />
            </Button>
            <Button size="sm" className="h-7 px-2" onClick={onAccept}>
              <Check className="size-3.5" />
            </Button>
          </div>
        )}
        {(approval.state === "applying" || approval.state === "rejecting") && (
          <Loader2 className="size-3.5 animate-spin text-muted-foreground" />
        )}
        {approval.state === "done" && (
          <span className="text-[11px] text-emerald-700 dark:text-emerald-400 flex items-center gap-1 shrink-0">
            <Check className="size-3" /> Appliqué
          </span>
        )}
        {approval.state === "error" && (
          <span
            className="text-[11px] text-destructive shrink-0"
            title={approval.error}
          >
            Erreur
          </span>
        )}
      </div>
      {approval.expanded && (
        <div className="mt-2 pt-2 border-t border-amber-500/20 text-[11.5px] space-y-0.5">
          {data.title && (
            <div>
              <span className="text-muted-foreground">Titre :</span>{" "}
              {data.title}
            </div>
          )}
          {data.phone_jsonb && data.phone_jsonb.length > 0 && (
            <div>
              <span className="text-muted-foreground">Téléphone :</span>{" "}
              {data.phone_jsonb.map((p) => p.number).join(", ")}
            </div>
          )}
          {data.email_jsonb && data.email_jsonb.length > 0 && (
            <div>
              <span className="text-muted-foreground">Email :</span>{" "}
              {data.email_jsonb.map((e) => e.email).join(", ")}
            </div>
          )}
          {data.background && (
            <div>
              <span className="text-muted-foreground">Note :</span>{" "}
              {data.background}
            </div>
          )}
          <div className="text-muted-foreground italic mt-1">
            {approval.summary}
          </div>
        </div>
      )}
    </div>
  );
}
